import axios from 'axios'
import { getSessionToken, getShopDomain, navigateFromApp } from './shopify'

const validShop = shop => typeof shop === 'string' && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop)
const embedded = () => window.self !== window.top
const launchError = () => Object.assign(new Error('Open Flype from Shopify Admin.'), { code: 'SHOPIFY_LAUNCH_INVALID' })

const navigateToShopify = (value, shop) => {
    const url = new URL(value)
    if (!validShop(shop) || url.protocol !== 'https:' || ![shop, 'admin.shopify.com'].includes(url.hostname) ||
        url.username || url.password || url.port) throw launchError()
    // A top-level legacy installation must reach OAuth before App Bridge can
    // authenticate it. Inside Admin, use App Bridge's top-level navigation.
    if (embedded()) navigateFromApp(url.href)
    else window.location.replace(url.href)
}

const handleInstallation = data => {
    if (!validShop(data?.shop) || (getShopDomain() && getShopDomain() !== data.shop)) throw launchError()
    if (data.status === 'install') {
        navigateToShopify(data.authorizationUrl, data.shop)
        return true
    }
    if (data.status !== 'ready') throw launchError()
    if (!embedded()) {
        navigateToShopify(data.appUrl, data.shop)
        return true
    }
    return false
}

// Returns true when navigation has started. This endpoint returns routing
// information only; it cannot issue credentials or authorize protected APIs.
export const prepareInstallation = async (launchQuery = window.location.search) => {
    const params = new URLSearchParams(launchQuery)
    if (params.has('hmac')) {
        try {
            const { data } = await axios.post('/auth', { launchQuery }, { timeout: 20000 })
            return handleInstallation(data)
        } catch (error) {
            // An old iframe URL can outlive its signed launch. A fresh Shopify
            // session is independent proof; the protected bootstrap still
            // verifies it before permitting any access or installation.
            if (embedded() && error.response?.status === 401 && error.response.data?.error === 'SHOPIFY_LAUNCH_INVALID') return false
            throw error
        }
    }
    if (!embedded()) {
        const shop = getShopDomain()
        const apiKey = process.env.GATSBY_SHOPIFY_APP_API_KEY
        if (!validShop(shop) || !apiKey) throw launchError()
        // Unsigned bookmarks can only open Shopify Admin, never initiate OAuth
        // or obtain credentials. Shopify supplies a signed launch from there.
        navigateToShopify(`https://${shop}/admin/apps/${encodeURIComponent(apiKey)}`, shop)
        return true
    }
    return false
}

// Handles an embedded launch without signed URL parameters. Only a verified
// Shopify session may start installation from this path.
export const prepareSessionInstallation = async () => {
    const token = await getSessionToken()
    const { data } = await axios.post('/auth', { session: true }, {
        timeout: 20000, headers: { Authorization: `Bearer ${token}` },
    })
    return handleInstallation(data)
}

// OAuth is used only to install/reconnect the app. Browser authentication is
// always performed separately using Shopify App Bridge, never cached tokens.
export const refreshAuth = async (shop = getShopDomain()) => {
    if (!validShop(shop)) throw new Error('Missing Shopify store')
    const { data } = await axios.post('/auth', { shop }, { timeout: 20000 })
    navigateToShopify(data.body, shop)
}
